import CircledBackButton from "@/features/Common/components/CircledBackButton";
import theme, { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import {
  Box,
  Divider,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import BottomFixedCallToAction from "../BottomFixedCallToAction";
import {
  formatAmount,
  getDiscountedAmountInCents,
  replaceCentsFromAmount,
} from "@/utils";
import {
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
} from "@/services/core-api-adapter";

function formatInput(voucherPin: string): string {
  return voucherPin
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, "$1 ");
}

interface OneVoucherRedemptionProps {
  memberProfile?: MemberProfile | undefined;
  selectedVoucherSubType?: any;
  responseError?: string;
  currentOneVoucherPin?: string;
  onBackButtonClick: () => void;
  onOneVoucherContinueButtonClick: (pin: string) => void;
  serviceOfferingType?:
    | "BloodTest"
    | "Voucher"
    | "DirectToDoctor"
    | "NurseConsult"
    | "DoctorMedicineBundle";
  discountAmount?: number;
  selectedServiceAmount?: string;
}

const errorCodes = ["2003", "2408"];

export default function OneVoucherRedemption({
  memberProfile,
  selectedVoucherSubType,
  responseError,
  currentOneVoucherPin,
  onBackButtonClick,
  onOneVoucherContinueButtonClick,
  serviceOfferingType,
  discountAmount = 0,
  selectedServiceAmount = "R0",
}: OneVoucherRedemptionProps) {
  const { t } = useTranslation();
  const [voucherPin, setVoucherPin] = useState<string>(
    formatInput(currentOneVoucherPin || "")
  );
  const [isVoucherPinValid, setIsVoucherPinValid] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!!responseError) {
      let errorCode;
      try {
        const errorObj =
          typeof responseError === "string"
            ? JSON.parse(responseError)
            : responseError;
        errorCode = errorObj.errorCode;
      } catch {
        errorCode = undefined;
      }
      const errorMessage = errorCodes.includes(errorCode)
        ? t("OneVoucherRedemption.enterOneVoucherPin.insufficientFunds")
        : t("OneVoucherRedemption.enterOneVoucherPin.genericError");
      setError(errorMessage);
    }
  }, [responseError]);

  function validateVoucherPin(pin: string = voucherPin) {
    const sanitizedPin = pin.replace(/\s/g, "");
    !/^\d{16}$/.test(sanitizedPin)
      ? setError(t("OneVoucherRedemption.enterOneVoucherPin.incorrectPin"))
      : setIsVoucherPinValid(true);
  }

  const directToDoctorPaymentRates =
    memberProfile?.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  const docMedBundlePaymentRates =
    memberProfile?.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
    );

  const nurseAssistedPaymentRates =
    memberProfile?.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
    );

  return (
    <Stack
      color="neutral.dark"
      sx={{
        ...defaultFullscreenPageStyling,
        minHeight: "100%",
        justifyContent: "space-between",
        flex: 1,
      }}
    >
      <Stack p={2} spacing={2}>
        <Stack>
          <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
        </Stack>
        <Stack mt={3} spacing={3}>
          <Typography
            variant="h2"
            component="h1"
            color="inherit"
            textAlign="start"
          >
            {t("OneVoucherRedemption.title")}
          </Typography>
          <Stack
            sx={{
              ...panelStyle,
            }}
            width="100%"
            color="neutral"
            textAlign="left"
          >
            <Stack spacing={2}>
              <Stack
                direction={"row"}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body1">
                  {serviceOfferingType === "Voucher"
                    ? t("OneVoucherRedemption.Voucher.subtitle", {
                        type: selectedVoucherSubType?.name,
                      })
                    : t(
                        `OneVoucherRedemption.${serviceOfferingType}.subtitle` as any
                      )}
                </Typography>
                <Typography
                  variant="body1"
                  color={theme.palette.primary.main}
                  fontWeight={600}
                >
                  {replaceCentsFromAmount(selectedServiceAmount)}
                </Typography>
              </Stack>
              {!!discountAmount && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Typography variant="h4" fontWeight={400}>
                    {t("OrderSummary.discountVoucherApplied.text")}
                  </Typography>
                  <Typography color="primary">
                    {`-${formatAmount(discountAmount, "ZAR")}`}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Stack
                direction={"row"}
                alignItems="center"
                justifyContent="space-between"
                aria-label={t("OneVoucherRedemption.orderTotal.title")}
              >
                <Typography variant="body1" fontWeight={700}>
                  {t("OneVoucherRedemption.orderTotal.title")}
                </Typography>
                <Typography
                  variant="body1"
                  fontSize="1.5rem"
                  fontWeight={700}
                  color={theme.palette.primary.main}
                >
                  {formatAmount(
                    getDiscountedAmountInCents(
                      selectedServiceAmount,
                      discountAmount
                    ),
                    "ZAR"
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            aria-label={t("OneVoucherRedemption.enterOneVoucherPin.title")}
            sx={{
              ...panelStyle,
            }}
            width="100%"
            color="neutral"
            textAlign="left"
          >
            <Stack spacing={1}>
              <Typography variant="h4">
                {t("OneVoucherRedemption.enterOneVoucherPin.title")}
              </Typography>
              <Typography variant="body2">
                <Trans i18nKey="OneVoucherRedemption.enterOneVoucherPin.description" />
              </Typography>
            </Stack>
            <Stack mt={2}>
              <InputLabel>
                <Typography
                  textAlign="left"
                  variant="body2"
                  color={error ? "error" : "neutral.main"}
                  pb={1}
                  fontWeight={600}
                >
                  {t("OneVoucherRedemption.enterOneVoucherPin.inputLabel")}
                </Typography>
              </InputLabel>
              <TextField
                placeholder={t(
                  "OneVoucherRedemption.enterOneVoucherPin.placeholder"
                )}
                sx={{
                  borderColor: "neutral.light",
                  borderRadius: "5px",
                  fontSize: "8px",
                }}
                slotProps={{
                  input: {
                    "aria-label": t(
                      "OneVoucherRedemption.enterOneVoucherPin.inputLabel"
                    ),
                  },
                }}
                variant="outlined"
                fullWidth
                value={voucherPin}
                size="small"
                error={!!error}
                helperText={error}
                onChange={(event) => {
                  setError(undefined);
                  setIsVoucherPinValid(false);
                  const formattedPin = formatInput(event.target.value);
                  setVoucherPin(formattedPin);
                  if (formattedPin.replace(/\s/g, "").length === 16) {
                    validateVoucherPin(formattedPin);
                  }
                }}
                onBlur={() => validateVoucherPin()}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <BottomFixedCallToAction
        buttonText="common.continueButton"
        onButtonClick={() => {
          setError(undefined);
          onOneVoucherContinueButtonClick(voucherPin.replace(/\s/g, ""));
        }}
        ariaLabel="common.continueButton"
        analyticsPageName="1Voucher Payment"
        isButtonEnabled={isVoucherPinValid}
      />
    </Stack>
  );
}
